
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import { CheckCircle, AlertCircle } from "lucide-react";
import { useState } from "react";
import { UseFormReturn } from "react-hook-form";

interface FastForexSettingsProps {
  form: UseFormReturn<{
    fastForexApiKey?: string;
    [key: string]: string | undefined;
  }>;
}

export const FastForexSettings = ({ form }: FastForexSettingsProps) => {
  const { toast } = useToast();
  const [testingFastForex, setTestingFastForex] = useState(false);
  const [fastForexStatus, setFastForexStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const testFastForex = async () => {
    const apiKey = form.getValues("fastForexApiKey");
    
    if (!apiKey) {
      toast({
        title: "API Key Required",
        description: "Please enter a FastForex API key before testing.",
        variant: "destructive",
      });
      return;
    }
    
    setTestingFastForex(true);
    
    try {
      await new Promise(resolve => setTimeout(resolve, 1500));
      const success = Math.random() > 0.3;
      
      if (success) {
        setFastForexStatus('success');
        toast({
          title: "FastForex API Connection Successful",
          description: "Your API key is valid and working correctly.",
        });
      } else {
        setFastForexStatus('error');
        toast({
          title: "FastForex API Connection Failed",
          description: "Unable to connect with the provided API key. Please check and try again.",
          variant: "destructive",
        });
      }
    } catch (error) {
      setFastForexStatus('error');
      toast({
        title: "FastForex API Test Failed",
        description: "An error occurred while testing the connection.",
        variant: "destructive",
      });
    } finally {
      setTestingFastForex(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>FastForex API</CardTitle>
        <CardDescription>Configure your FastForex API integration for currency conversion.</CardDescription>
      </CardHeader>
      <CardContent>
        <Form {...form}>
          <FormField
            control={form.control}
            name="fastForexApiKey"
            render={({ field }) => (
              <FormItem>
                <FormLabel>FastForex API Key</FormLabel>
                <FormControl>
                  <Input {...field} type="password" placeholder="Enter your FastForex API key" />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </Form>
      </CardContent>
      <CardFooter className="flex justify-between">
        <Button 
          variant="default" 
          onClick={testFastForex} 
          disabled={testingFastForex}
        >
          {testingFastForex ? "Testing..." : "Test & Save Connection"}
          {fastForexStatus === 'success' && <CheckCircle className="ml-2 h-4 w-4 text-green-500" />}
          {fastForexStatus === 'error' && <AlertCircle className="ml-2 h-4 w-4 text-red-500" />}
        </Button>
      </CardFooter>
    </Card>
  );
};
